import { ArrowClockwise, CheckCircle, Lock, Spinner, WarningCircle } from "@phosphor-icons/react";

/**
 * The four states every data surface must have a designed answer for: loading, empty, error and
 * refused.
 *
 * They live in one component because the prototype gate treated them as part of the design rather
 * than as leftovers, and because a refusal and an error must never look the same. An error invites
 * a retry. A refusal does not — offering a retry button for something the server will always refuse
 * teaches the client that the portal is unreliable rather than that they lack access.
 */

export type StateKind = "loading" | "empty" | "error" | "denied";

const PRESENTATION: Record<StateKind, { icon: typeof Spinner; role: "status" | "alert" }> = {
  loading: { icon: Spinner, role: "status" },
  empty: { icon: CheckCircle, role: "status" },
  error: { icon: WarningCircle, role: "alert" },
  denied: { icon: Lock, role: "status" }
};

export interface StateBlockProps {
  kind: StateKind;
  title: string;
  body?: string;
  compact?: boolean;
  onRetry?: () => void;
}

export function StateBlock({ kind, title, body, compact = false, onRetry }: StateBlockProps) {
  const { icon: Icon, role } = PRESENTATION[kind];

  return (
    <section
      className={`state-block state-${kind}${compact ? " is-compact" : ""}`}
      role={role}
      aria-live={role === "alert" ? "assertive" : "polite"}
      data-testid={`state-${kind}`}
    >
      <span className={`state-icon${kind === "loading" ? " is-spinning" : ""}`} aria-hidden="true">
        <Icon size={22} />
      </span>
      <h2>{title}</h2>
      {body ? <p>{body}</p> : null}
      {onRetry && kind === "error" ? (
        <button type="button" className="secondary-button" onClick={onRetry}>
          <ArrowClockwise size={16} aria-hidden="true" />
          <span>Försök igen</span>
        </button>
      ) : null}
    </section>
  );
}
